<template>
  <div>
    <v-row class="mb-4">
      <v-col>
        <h3 class="text-h6 font-weight-medium mb-4">
          Notifications
        </h3>
        <p class="text--secondary">
          Manage your notification preferences and alert settings.
        </p>
      </v-col>
    </v-row>

    <!-- Email Notifications -->
    <v-card
      outlined
      class="mb-4"
    >
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold mb-3">
          <font-awesome-icon
            :icon="['fas', 'envelope']"
            class="mr-2"
          />
          Email Notifications
        </div>

        <v-row
          align="center"
          class="my-1"
        >
          <v-col>
            <div class="text-body-2">
              Email notifications
            </div>
            <div class="caption text--secondary">
              Receive email notifications for important updates
            </div>
          </v-col>
          <v-col class="col-auto">
            <v-switch
              :input-value="localNotifications.email.enabled"
              color="primary"
              hide-details
              @change="updateField('email', 'enabled', $event)"
            />
          </v-col>
        </v-row>

        <v-row
          align="center"
          class="my-1"
        >
          <v-col>
            <div class="text-body-2">
              Email digest
            </div>
            <div class="caption text--secondary">
              Receive a summary of activity
            </div>
          </v-col>
          <v-col
            class="col-auto"
            cols="4"
          >
            <v-select
              :value="localNotifications.email.digest"
              :items="digestOptions"
              dense
              outlined
              hide-details
              :disabled="!localNotifications.email.enabled"
              @change="updateField('email', 'digest', $event)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- System Alerts -->
    <v-card outlined>
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold mb-3">
          <font-awesome-icon
            :icon="['fas', 'triangle-exclamation']"
            class="mr-2"
          />
          System Alerts
        </div>

        <v-row
          align="center"
          class="my-1"
        >
          <v-col>
            <div class="text-body-2">
              System maintenance
            </div>
            <div class="caption text--secondary">
              Planned maintenance and downtime notices
            </div>
          </v-col>
          <v-col class="col-auto">
            <v-switch
              :input-value="localNotifications.alerts.systemMaintenance"
              color="primary"
              hide-details
              @change="updateField('alerts', 'systemMaintenance', $event)"
            />
          </v-col>
        </v-row>

        <v-row
          align="center"
          class="my-1"
        >
          <v-col>
            <div class="text-body-2">
              Security updates
            </div>
            <div class="caption text--secondary">
              Important security and account notifications
            </div>
          </v-col>
          <v-col class="col-auto">
            <v-switch
              :input-value="localNotifications.alerts.securityUpdates"
              color="primary"
              hide-details
              @change="updateField('alerts', 'securityUpdates', $event)"
            />
          </v-col>
        </v-row>

        <v-row
          align="center"
          class="my-1"
        >
          <v-col>
            <div class="text-body-2">
              Feature updates
            </div>
            <div class="caption text--secondary">
              New features and improvements
            </div>
          </v-col>
          <v-col class="col-auto">
            <v-switch
              :input-value="localNotifications.alerts.featureUpdates"
              color="primary"
              hide-details
              @change="updateField('alerts', 'featureUpdates', $event)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row class="mt-4">
      <v-col class="d-flex justify-end">
        <v-btn
          color="primary"
          :disabled="!hasChanges"
          :loading="loading"
          @click="handleSubmit"
        >
          <font-awesome-icon
            :icon="['fas', 'floppy-disk']"
            class="mr-2"
          />
          Save Notification Preferences
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
const defaultNotifications = {
  email: { enabled: true, digest: 'daily', marketing: false },
  push: { enabled: true, browser: true, mobile: false },
  alerts: { systemMaintenance: true, securityUpdates: true, featureUpdates: false },
}

export default {
  name: 'SettingsNotifications',
  props: {
    notifications: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localNotifications: JSON.parse(JSON.stringify(defaultNotifications)),
      digestOptions: [
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Never', value: 'never' },
      ],
    }
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.localNotifications) !== JSON.stringify(this.mergedNotifications)
    },
    mergedNotifications() {
      return {
        email: { ...defaultNotifications.email, ...this.notifications?.email },
        push: { ...defaultNotifications.push, ...this.notifications?.push },
        alerts: { ...defaultNotifications.alerts, ...this.notifications?.alerts },
      }
    },
  },
  watch: {
    notifications: {
      handler() {
        this.localNotifications = JSON.parse(JSON.stringify(this.mergedNotifications))
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    updateField(section, field, value) {
      this.localNotifications[section][field] = value
    },
    handleSubmit() {
      if (this.hasChanges) {
        this.$emit('update', this.localNotifications)
      }
    },
  },
}
</script>
